import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Fees / Billing → Fee Adjustment (One-Time) / Fee Refund /
 * Fee Schedule Change.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Fee Refund Information"
 * (Fee_Refund_Information_Screen), "Fee Schedule Change Details"
 * (Fee_Schedule_Change), "Fee Adjustment (One-Time) Details"
 * (Fee_Adjustment_One_Time), then the matching "Attach Fee Refund Files" /
 * "Attach Fee Schedule Change Files" / "Attach Fee Adjustment Files" screen.
 * The three sub-categories share one shape - a run of questions, Entire
 * Household vs Specific Accounts, an instructions box, an optional sleeve
 * table (Fee_Sleeve_Collection), then an upload + acknowledgement screen - so
 * they share one guide with a question list per sub-category. Shared chrome
 * and policy copy live in GuideKit.js.
 */

const SCHEDULE_A_NOTICE = 'Please note, if there is a fee schedule change (Whether One-Time or On-going) you must provide a signed updated Schedule A in order for us to process. We legally can NOT change the fee schedule without this. For changes that result in a lower Fee Schedule, we only require a Schedule A signed by the Advisor, otherwise if higher, a signed Schedule A from the Client and Advisor.';

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Fees / Billing',
    subCategory: 'Fee Adjustment (One-Time), Fee Refund or Fee Schedule Change',
    subject: subjectRow('Fee refund - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>Each Sub-Category has its own questions</strong> - see the reference below. Fee Schedule Change
        and Fee Adjustment (One-Time) are nearly identical; Fee Refund asks about the refund itself.
      </>
    ),
  }),
  {
    title: 'Answer the details questions',
    body: (
      <p style={{ margin: 0 }}>
        The next screen is titled <strong>Fee Refund Information</strong>, <strong>Fee Schedule Change
        Details</strong> or <strong>Fee Adjustment (One-Time) Details</strong>. Questions reveal themselves one at
        a time as you answer, so you only see the next one once the last is filled in. Every question is required
        once it appears.
      </p>
    ),
  },
  {
    title: 'Say which accounts it covers',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          Near the end, <em>Is this for the entire Household, specific Accounts, or Neither / Something Else?</em>{' '}
          decides whether you pick accounts, followed by a large instructions box. Describe the request in full there
          - billing periods, rates, amounts and which accounts.
        </p>
        <p style={{ margin: 0 }}>
          If you choose <strong>Specific Accounts</strong>, a table appears (<em>Fee Refund Accounts</em>,{' '}
          <em>Fee Schedule Accounts</em> or <em>Fee Adjustment Accounts</em>). Tick every account the request
          covers - you can tick as many as you need. It shows Name, Sleeve Details, Fee Schedule, Model and Balance.
          The form lets you continue with nothing ticked, so check before you click Next.
        </p>
      </>
    ),
  },
  {
    title: 'Attach the documents',
    body: (
      <p style={{ margin: 0 }}>
        The next screen (<strong>Attach Fee Refund Files</strong>, <strong>Attach Fee Schedule Change
        Files</strong> or <strong>Attach Fee Adjustment Files</strong>) has upload tiles and the message{' '}
        <em>Please add any related Documents to the tiles below. Please note, missing or incomplete documents may
        result in a NIGO.</em> Add the signed Schedule A here if the fee schedule is changing, plus anything else
        that supports the request. The files are attached to the case when it is created.
      </p>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the upload tiles - it governs timing and what the teams can
        and cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

const HOUSEHOLD_OPTIONS = ['Entire Household', 'Specific Accounts', 'Something Else'];
const SCHEDULE_A_OPTIONS = [
  'Yes (I have a signed updated Schedule A)',
  'No (I do not have a signed updated Schedule A)',
  'Not Applicable (I understand this will result in a NIGO if a Schedule A is required)',
];
const SCHEDULE_A_NOTE = 'The form lets you continue with any answer, and the document itself goes on the next screen. If the fee schedule is changing and there is no signed Schedule A, the request is returned NIGO - have it signed before you submit.';
const TABLE_NOTE = 'A table - tick one or more rows. Columns: Name, Sleeve Details, Fee Schedule, Model, Balance. It lists the household\'s active sleeves with a balance of zero or more. Cancelled sleeves (ending in CNX), the Contribution and Distribution holding sleeves, and the custodial account row itself are left out.';

// Every question on each sub-category's details screen, in the order it can appear.
const QUESTIONS = [
  // Fee Refund - Fee Refund Information
  {
    screen: 'Fee Refund - Fee Refund Information',
    q: 'Is this request to refund the Advisor, Client, or something else?',
    when: 'Always - this is the first question',
    options: ['Refund Advisor', 'Refund Client', 'Something Else'],
  },
  {
    q: 'Refund / Request Amount',
    when: 'Once you have answered who is being refunded',
    note: 'A dollar amount. It is recorded on the case.',
  },
  {
    q: 'Was this an error that the Prosperity Team made?',
    when: 'Once you have answered who is being refunded',
    options: ['Yes (This was a Prosperity Error)', 'No (This was not a Prosperity Error)', 'No (This is not due to an Error)'],
  },
  {
    q: 'Additional Details',
    when: 'Once the error question is answered',
    note: 'A single line. On a refund this becomes the case description Operations reads first, so lead with the essentials.',
  },
  {
    q: 'Is this request tied to a specific billing period or multiple billing periods? (Example: The Household was charged the wrong fee for several months vs. One-Time)',
    when: 'Once Additional Details has been filled in',
    options: ['One-Time (Specific Billing Period)', 'More than One (Multiple Billing Periods)', 'Other'],
  },
  {
    q: 'Is this for the entire Household, specific Accounts, or Neither / Something Else?',
    when: 'Once the billing period question is answered',
    options: HOUSEHOLD_OPTIONS,
  },
  {
    q: 'Please provide all relevant information',
    when: 'Once the Household question is answered',
    note: 'The large box. Name the billing period(s) and how the amount was worked out.',
  },
  {
    q: 'Fee Refund Accounts',
    when: 'Only if you chose Specific Accounts, once the box above has been filled in',
    note: TABLE_NOTE,
  },

  // Fee Schedule Change - Fee Schedule Change Details
  {
    screen: 'Fee Schedule Change - Fee Schedule Change Details',
    q: 'Will this change result in a Higher or Lower Fee Schedule??',
    when: 'Always - this is the first question, under the Schedule A notice',
    options: ['Higher Fee Schedule', 'Lower Fee Schedule', 'Blended Fee Schedule'],
  },
  {
    q: 'Additional Details',
    when: 'Once the Higher or Lower question is answered',
    note: 'A single line - the new rate or schedule in brief.',
  },
  {
    q: 'Do you have a Signed Schedule A by the Advisor and/or Client?',
    when: 'Once Additional Details has been filled in',
    options: SCHEDULE_A_OPTIONS,
    note: SCHEDULE_A_NOTE,
    warn: true,
  },
  {
    q: 'Reason / Additional Information',
    when: 'Once the Schedule A question is answered',
    note: 'A single line - why the schedule is changing.',
  },
  {
    q: 'Is this for the entire Household, specific Accounts, or Neither / Something Else?',
    when: 'Once Reason / Additional Information has been filled in',
    options: HOUSEHOLD_OPTIONS,
  },
  {
    q: 'Please provide as much information as possible for the Operations Team...',
    when: 'Once the Household question is answered',
    note: 'The large box. This becomes the case description Operations reads first - give the new schedule in full and when it should take effect.',
  },
  {
    q: 'Fee Schedule Accounts',
    when: 'As soon as you choose Specific Accounts',
    note: TABLE_NOTE,
  },

  // Fee Adjustment (One-Time) - Fee Adjustment (One-Time) Details
  {
    screen: 'Fee Adjustment (One-Time) - Fee Adjustment (One-Time) Details',
    q: 'Will this change result in a Higher or Lower Fee?',
    when: 'Always - this is the first question, under the Schedule A notice',
    options: ['Higher Fee Schedule', 'Lower Fee Schedule', 'Blended Fee Schedule'],
  },
  {
    q: 'What is the reason for the fee adjustment?',
    when: 'Once the Higher or Lower question is answered',
    note: 'A single line.',
  },
  {
    q: 'Do you have a Signed Schedule A by the Advisor and/or Client?',
    when: 'Once the reason has been filled in',
    options: SCHEDULE_A_OPTIONS,
    note: SCHEDULE_A_NOTE,
    warn: true,
  },
  {
    q: 'Reason / Additional Information',
    when: 'Once the Schedule A question is answered',
    note: 'A single line.',
  },
  {
    q: 'Is this for the entire Household, specific Accounts, or Neither / Something Else?',
    when: 'Once Reason / Additional Information has been filled in',
    options: HOUSEHOLD_OPTIONS,
  },
  {
    q: 'Please provide as much information as possible for the Operations Team....',
    when: 'Once the Household question is answered',
    note: 'The large box. This becomes the case description Operations reads first - give the billing period and the adjusted amount or rate.',
  },
  {
    q: 'Fee Adjustment Accounts',
    when: 'Only if you chose Specific Accounts, once the box above has been filled in',
    note: TABLE_NOTE,
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Fees / Billing'),
  {
    h: "The account I expected isn't in the list",
    p: [
      'The account tables list sleeves, not just the account as a whole - you will see rows like 0989-022356_DFA TM 100/0. The Fee Schedule column shows the current fee schedule in FSC for each sleeve. Tick every sleeve the request covers.',
      'Inactive sleeves, sleeves with a negative balance, cancelled sleeves ending in CNX, the Contribution and Distribution holding sleeves, and the custodial account row itself are filtered out.',
    ],
  },
  {
    h: "The account table hasn't appeared",
    p: [
      'It only appears if you chose Specific Accounts. On Fee Refund and Fee Adjustment (One-Time) it also waits until the large instructions box has something in it.',
    ],
  },
  {
    h: "The next question hasn't appeared",
    p: ['Each question waits for the one above it. A text box counts as answered only once something is typed in it.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function FeesBilling() {
  return (
    <GuidePage
      title="Fees and Billing Requests"
      icon="receipt"
      path="Operations → Fees / Billing → Fee Adjustment (One-Time), Fee Refund or Fee Schedule Change"
      minutes={6}
      intro="How to submit a fee refund, a one-time fee adjustment or a fee schedule change to the Prosperity Operations team - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers, and missing or incomplete documents may result in a NIGO.</Para>
        <Bullets items={[
          <><strong>Which request it is</strong> - Fee Refund, Fee Adjustment (One-Time) or Fee Schedule Change. Each is its own Sub-Category with its own questions.</>,
          <><strong>Whether it covers the entire household or specific accounts.</strong> For specific accounts you tick them from a list, so you do not need account numbers memorised.</>,
          <><strong>For a refund</strong> - who is being refunded, the amount, whether it was a Prosperity error, and the billing period(s).</>,
          <><strong>For a schedule change or adjustment</strong> - whether the fee goes higher, lower or blended, and the reason.</>,
          <><strong>A signed, updated Schedule A</strong> if the fee schedule is changing, ready to upload.</>,
        ]} />
        <Callout tone="warn">
          The details screen for every Fees / Billing sub-category shows this notice: <em>{SCHEDULE_A_NOTICE}</em>
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Questions are grouped by Sub-Category. Use this to prepare, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe fee policy, how refunds are paid, or
        when a changed schedule first applies to billing.
      </Footnote>
    </GuidePage>
  );
}
